// tslint:disable: no-console
import { Kaggle } from "@kaggle/sdk";
import { getServices } from "../../../api/getServices";
import {
  buildRunRequest,
  CreateRunRequest,
} from "../../../api/kernel/buildRunRequest";
import { editorFrameClient } from "../../pages/editor/editorFrameClient";
import { EditorStatus } from "../../types/EditorStatus";
import TerminalMessage from "../../types/TerminalMessage";
import {
  getCurrentDataSourceReferences,
  getCurrentDataSources,
  getCurrentKernelRunId,
} from "../selectors/current";
import { getStore } from "../store/appStore";
import { AppState } from "../types";
import { editorQueue } from "../utils/editorSnackbarQueue";
import { setEditorStatus } from "./editor";
import { updateLocalSessions } from "./local";
import { setDataSourceReferences, setIsInternetEnabled, stopRun } from "./notebook";
import { cancelKernelRun } from "./notebook/cancelKernelRun";
import { requestWorker } from "./notebook/requestWorker";
import { updateKernelRun } from "./notebook/updateKernelRun";
import { appendTerminal, appendTerminalError } from "./terminal";

type DataSource = Kaggle.Sdk.Datasets.Databundles.Models.DataObjects.DataSource;
type DataSourceReference = Kaggle.Sdk.Kernels.Models.DataSourceReference;

/**
 * Selector that detects whether the worker is running.
 */
export const isRunning = (state: AppState) =>
  state.notebook.run !== null && state.editor.status === EditorStatus.RUNNING;

/**
 * Selector that detects whether the worker is stopped.
 */
export const isStopped = (state: AppState) =>
  state.editor.status === EditorStatus.STOPPED;

/**
 * Start the worker. Returns the run.
 *
 * Throws if the worker is already running.
 */
export const startWorker = async (
  runParams: Partial<CreateRunRequest> = {}
) => {
  const { getState } = getStore();
  try {
    if (isRunning(getState())) {
      throw new Error("Kernel worker is already running.");
    }

    appendTerminal([new TerminalMessage("Session is starting...")]);
    editorQueue.notify({
      title: "Session is starting...",
      timeout: 4000,
      leading: true,
    });

    await requestWorker(buildRunRequest(getState())(runParams));

    appendTerminal([new TerminalMessage("Session started.")]);
  } catch (error) {
    appendTerminalError("An error occurred starting the worker.", error);
    throw error;
  }
};

/**
 * Stop the worker if its running and clear redux.
 */
export const stopWorker = async () => {
  const services = getServices();
  try {
    setEditorStatus(EditorStatus.STOPPED);
    await editorFrameClient.shutdownKernel();

    // Get the kernelRunId prior to stopping the run.
    const kernelRunId = getCurrentKernelRunId(getStore().getState());

    appendTerminal([new TerminalMessage("Session is stopping...")]);

    stopRun();
    try {
      // If the run id existed, stop it.
      if (kernelRunId) {
        await services.kernelsClient.cancelKernelRun({ kernelRunId });
      }
    } catch {
      console.error("Could not cancel the kernel run due to network error.");
    }

    appendTerminal([new TerminalMessage("Session stopped.")]);
  } catch (error) {
    appendTerminalError("An error occurred stopping the worker.", error);
    throw error;
  }
};

/**
 * Stop and start the worker.
 */
export const restartWorker = async (
  runParams: Partial<CreateRunRequest> = {}
) => {
  const state = getStore().getState();

  try {
    if (state.notebook.run) {
      // Stop the worker.
      await stopWorker();
    }

    // Start the worker.
    await startWorker(runParams);
  } catch (error) {
    appendTerminalError("An error occurred restarting the worker.", error);
    throw error;
  }
  // Update local sessions so they're in sync with
  // the change just made.
  updateLocalSessions();
};

export const cancelWorkerRequest = () => {
  const state = getStore().getState();
  try {
    setEditorStatus(EditorStatus.STOPPED);

    const kernelRunId = state.notebook.run;
    if (kernelRunId) {
      const cancelRunRequest = { kernelRunId } as any;
      cancelKernelRun(cancelRunRequest);
    }
  } catch (error) {
    appendTerminalError("An error occurred cancelling the worker.", error);
    throw error;
  }
};

/**
 * Update the worker/session.
 */
export const updateWorker = async (args: {
  updateDatasets?: boolean,
  useLegacyOnly?: boolean,
  updateInternet?: boolean,
}) => {
  const { getState } = getStore();
  try {
    const kernelRunId = getState().notebook.run;
    if (kernelRunId) {
      appendTerminal([new TerminalMessage("Updating kernel worker...")]);

      const dataSources = !args.useLegacyOnly
        ? mapSourcesToSourceReferences(getCurrentDataSources(getState()))
        : getCurrentDataSourceReferences(getState());

      const updateRunRequest: Kaggle.Sdk.Kernels.Requests.UpdateKernelRunRequest = {
        kernelRunId,
        isInternetEnabled: getState().notebook.internet,
        dataSources,
      };

      const run = await updateKernelRun(updateRunRequest);
      if (run.restartRequired) {
        await restartWorker();
        return;
      }
      setDataSourceReferences(run.dataSources);

      appendTerminal([new TerminalMessage("Kernel worker updated.")]);
    }
  } catch (error) {
    appendTerminalError("An error occurred updating the session.", error);
    
    if (args.updateInternet) {
      // Any error should toggle back the switch, if this parameter was updated.
      // The only weird situation would be if the backend applied the change,
      // but the change wasn't saved to the database. By toggling back in this
      // case, the user will retry the same change.
      setIsInternetEnabled(!getState().notebook.internet);
    }
    
    // Do not throw error, as some callers don't handle it.
  }
};

/*
 * The existing Kernels APIs for removing a data source expect the legacy
 * DataSourceReference type. Once DataExplorerV3 is live in the editor,
 * migrate the FE and BE to both use only the new types.
 */
export const mapSourcesToSourceReferences = (
  sources: DataSource[]
): DataSourceReference[] => {
  return sources.map(source => ({
    sourceType: getSourceType(source),
    sourceId:
      source.sourceType === "dataset"
        ? source.databundleVersion.datasetVersionInfo?.datasetVersionId || 0
        : source.sourceId,
    datasetId: source.databundle.datasetInfo?.datasetId || null,
    databundleVersionId:
      source.databundle.databundleInfo.currentDatabundleVersionId || null,
    mountSlug: source.mountSlug,
  }));
};

const getSourceType = (
  source: DataSource
): "competition" | "datasetVersion" | "kernelVersion" => {
  switch (source.sourceType) {
    case "competition":
      return "competition";
    case "dataset":
      return "datasetVersion";
    case "notebook":
    case "notebookOutput":
    case "utilityScript":
      return "kernelVersion";
    default:
      throw new Error(
        "Could not remove data source because it does not have a recognized type."
      );
  }
};
